// NOTICE: KAPTION-INTEGRATION
// Composable for managing scheduled messages in a conversation
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import KaptionScheduledMessagesAPI from 'dashboard/api/kaptionScheduledMessages';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

/**
 * Composable for managing scheduled messages per conversation
 *
 * @param {import('vue').Ref<number>} conversationId - The conversation ID ref
 * @returns {Object} State and methods for scheduled messages
 */
export function useKaptionScheduledMessages(conversationId) {
  const store = useStore();
  const { canUseFeature } = useFeaturePermissions();
  const scheduledMessages = ref([]);
  const isFetching = ref(false);
  const isCancelling = ref(false);

  const accountId = computed(() => store.getters.getCurrentAccountId);

  // NOTICE: KAPTION-INTEGRATION - feature-permissions.REPLY_BOX.1 / CORE.2
  // The index endpoint carries the same authorize_feature!('scheduled_messages')
  // gate as create, so an account without the feature could only ever get a 401
  // feature_not_permitted back from the fetch below. canUseFeature reads the
  // effective map (flag AND permission, already combined server-side), which is
  // exactly what the controller resolves, and fails open while the account
  // payload is absent — so a permitted account keeps fetching as before.
  const canUseScheduledMessages = computed(() =>
    canUseFeature('scheduled_messages')
  );

  const scheduledMessagesCount = computed(() => scheduledMessages.value.length);

  const fetchScheduledMessages = async () => {
    // NOTICE: KAPTION-INTEGRATION - never ask for a feature the server will refuse.
    if (
      !conversationId.value ||
      !accountId.value ||
      !canUseScheduledMessages.value
    ) {
      scheduledMessages.value = [];
      return;
    }

    isFetching.value = true;
    try {
      const response = await KaptionScheduledMessagesAPI.getScheduledMessages(
        accountId.value,
        conversationId.value
      );
      // The API returns { scheduled_messages: [...] }
      scheduledMessages.value = response.data.scheduled_messages || [];
    } catch (error) {
      // Silent failure
      scheduledMessages.value = [];
    } finally {
      isFetching.value = false;
    }
  };

  const cancelScheduledMessage = async messageId => {
    if (!conversationId.value || !accountId.value) return;

    isCancelling.value = true;
    try {
      await KaptionScheduledMessagesAPI.cancelScheduledMessage(
        accountId.value,
        conversationId.value,
        messageId
      );
      await fetchScheduledMessages();
    } finally {
      isCancelling.value = false;
    }
  };

  // Watch for conversationId or accountId changes
  // NOTICE: KAPTION-INTEGRATION - canUseScheduledMessages is watched too so the
  // list still loads if the permission map lands after this composable mounts.
  watch(
    [conversationId, accountId, canUseScheduledMessages],
    () => {
      fetchScheduledMessages();
    },
    { immediate: true }
  );

  // Listen for 'kaption-message-scheduled' event to refresh the list
  const handleMessageScheduled = event => {
    if (event.conversationId === conversationId.value) {
      fetchScheduledMessages();
    }
  };

  onMounted(() => {
    emitter.on('kaption-message-scheduled', handleMessageScheduled);
  });

  onUnmounted(() => {
    emitter.off('kaption-message-scheduled', handleMessageScheduled);
  });

  return {
    scheduledMessages,
    scheduledMessagesCount,
    isFetching,
    isCancelling,
    refresh: fetchScheduledMessages,
    cancelScheduledMessage,
  };
}
